import { useEffect, useMemo, useRef, useState } from "react";
import type { ContentBundle, Locale } from "../api/types";
import { AudiencePattern } from "../patterns/AudiencePattern";
import { CounterTicker } from "../components/CounterTicker";
import { LanguagePicker } from "../components/LanguagePicker";
import { FallbackBanner } from "../components/FallbackBanner";
import { localUi } from "../i18n/uiFallback";

interface Props {
  bundle: ContentBundle;
  audiencePattern: string;
  tone: "soft" | "direct";
  locales: Locale[];
  requestedLocale: string;
  onLocale: (code: string) => void;
  filterCat: string;
  setFilterCat: (v: string) => void;
  savedIds: string[];
  toggleSaved: (id: string) => void;
  onBack: () => void;
  onSwitchAudience: () => void;
}

export function LetterScreen({
  bundle,
  audiencePattern,
  tone,
  locales,
  requestedLocale,
  onLocale,
  filterCat,
  setFilterCat,
  savedIds,
  toggleSaved,
  onBack,
  onSwitchAudience,
}: Props) {
  const t = (k: string) => bundle.ui[k] ?? k;
  const expRef = useRef<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState(0);

  const flowSlugs = bundle.flow.map((f) => f.slug);
  const visibleCats = filterCat === "all" ? flowSlugs : flowSlugs.filter((c) => c === filterCat);

  const chartCats = new Set(["climate", "hunger", "science"]);
  const chartAtIdx = visibleCats.findIndex((c) => chartCats.has(c));
  const storyAtIdx = Math.max(0, Math.floor(visibleCats.length / 2));
  const faqAtIdx = Math.max(0, visibleCats.length - 1);

  useEffect(() => {
    const el = expRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      const max = el.scrollHeight - el.clientHeight;
      setProgress(max > 0 ? el.scrollTop / max : 0);
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [bundle.audience_slug]);

  const chart = useMemo(() => {
    // pick the chart that fits the chapter we inject it under
    if (chartAtIdx < 0) return null;
    const cat = visibleCats[chartAtIdx];
    if (cat === "hunger") return bundle.charts.find((c) => c.slug === "land") ?? null;
    return bundle.charts.find((c) => c.slug === "climate") ?? null;
  }, [bundle.charts, chartAtIdx, visibleCats]);

  return (
    <div className="experience" ref={expRef}>
      <div className="exp__progress" style={{ width: `${progress * 100}%` }} />

      <div className="exp__topbar">
        <button className="ghost mono" onClick={onBack}>
          ← {t("back")}
        </button>
        <div className="exp__topbar-mid">
          <LanguagePicker
            locales={locales}
            value={requestedLocale}
            onChange={onLocale}
            label={t("lang")}
          />
        </div>
        <button className="ghost mono" onClick={onSwitchAudience}>
          {t("switch_audience")} →
        </button>
      </div>

      <FallbackBanner
        bundle={bundle}
        requestedLocale={requestedLocale}
        requestedLabel={locales.find((l) => l.code === requestedLocale)?.display_name}
      />

      <section className="exp__cover">
        <div className="cover__bg" aria-hidden="true">
          <AudiencePattern id={audiencePattern} />
        </div>
        <div className="cover__meta mono">
          <span>{t("open_letter").toUpperCase()}</span>
          <span>·</span>
          <span>{bundle.locale.toUpperCase()}</span>
          <span>·</span>
          <span>{tone === "direct" ? t("tone_direct") : t("tone_soft")}</span>
        </div>
        <div className="cover__to mono">{t("to")}</div>
        <h1 className="cover__title">{bundle.audience_label}</h1>
        <div className="cover__letter">
          {bundle.letter_paragraphs.map((p, i) => (
            <p key={i} className={i === 0 ? "lede" : ""}>
              {p}
            </p>
          ))}
        </div>
        <div className="cover__scroll mono">↓ {localUi(bundle.locale, "continue")}</div>
      </section>

      <section className="exp__filter">
        <div className="filter__label mono">{t("filter_title")}</div>
        <div className="filter__chips">
          <button
            className={`chip mono ${filterCat === "all" ? "chip--on" : ""}`}
            onClick={() => setFilterCat("all")}
          >
            {t("filter_all")}
          </button>
          {bundle.flow.map((c) => (
            <button
              key={c.slug}
              className={`chip mono ${filterCat === c.slug ? "chip--on" : ""}`}
              onClick={() => setFilterCat(c.slug)}
            >
              <span className="chip__glyph">{c.glyph}</span>
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {visibleCats.map((catSlug, cIdx) => {
        const args = bundle.arguments_by_category[catSlug] ?? [];
        const cat = bundle.flow.find((c) => c.slug === catSlug)!;
        return (
          <div key={catSlug}>
            <section className="chapter">
              <div className="chapter__head">
                <div className="chapter__num mono">
                  {t("chapter").toUpperCase()} {String(cIdx + 1).padStart(2, "0")} /{" "}
                  {String(visibleCats.length).padStart(2, "0")}
                </div>
                <div className="chapter__glyph">{cat.glyph}</div>
                <h2 className="chapter__title">{cat.label}</h2>
              </div>
              <div className="chapter__args">
                {args.length === 0 ? (
                  <article className="arg">
                    <p className="arg__b">{t("filter_all")}</p>
                  </article>
                ) : (
                  args.map((a, i) => (
                    <ArgumentCard
                      key={a.slug}
                      a={a}
                      idx={i}
                      total={args.length}
                      t={t}
                      locale={bundle.locale}
                      saved={savedIds.includes(a.slug)}
                      toggleSaved={() => toggleSaved(a.slug)}
                    />
                  ))
                )}
              </div>
            </section>

            {cIdx === chartAtIdx && chart && (
              <section className="exp__chart-section">
                <div className="chart__head">
                  <div className="mono">— {t("stats_title").toUpperCase()}</div>
                  <h3 className="chart__title">{chart.title}</h3>
                  {chart.source && <div className="chart__src mono">{chart.source}</div>}
                </div>
                <div className="chart__bars">
                  {(() => {
                    const max = Math.max(...chart.bars.map((b) => b.value));
                    return chart.bars.map((b, i) => {
                      const w = (b.value / max) * 100;
                      return (
                        <div
                          key={b.label}
                          className={`bar ${b.is_plant ? "bar--plant" : "bar--meat"}`}
                        >
                          <div className="bar__label mono">{b.label}</div>
                          <div className="bar__track">
                            <div
                              className="bar__fill"
                              style={{ width: `${w}%`, transitionDelay: `${i * 60}ms` }}
                            />
                          </div>
                          <div className="bar__v mono">{b.value.toLocaleString(bundle.locale)}</div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </section>
            )}

            {cIdx === storyAtIdx && bundle.story && (
              <section className="exp__story">
                <div className="story">
                  <div className="story__portrait">
                    <div className="story__portrait-letter">{bundle.story.portrait}</div>
                    <svg className="story__portrait-pattern" viewBox="0 0 100 100">
                      {Array.from({ length: 12 }).map((_, i) => (
                        <circle
                          key={i}
                          cx={50}
                          cy={50}
                          r={5 + i * 4}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={0.5}
                          opacity={0.6 - i * 0.04}
                        />
                      ))}
                    </svg>
                  </div>
                  <div className="story__body">
                    <div className="mono">— {t("story_title").toUpperCase()}</div>
                    <blockquote className="story__quote">
                      <span className="story__open">“</span>
                      {bundle.story.quote}
                      <span className="story__close">”</span>
                    </blockquote>
                    <div className="story__name">{bundle.story.name}</div>
                    <div className="story__role mono">{bundle.story.role}</div>
                  </div>
                </div>
              </section>
            )}

            {cIdx === faqAtIdx && bundle.faqs.length > 0 && (
              <section className="exp__faq">
                <FaqList faqs={bundle.faqs} t={t} />
              </section>
            )}
          </div>
        );
      })}

      <section className="exp__start">
        <div className="start__head">
          <div className="mono">— {t("start_title").toUpperCase()}</div>
          <h2 className="start__title">{t("start_title")}</h2>
          <p className="start__sub">{t("start_sub")}</p>
        </div>
        <ol className="start__steps">
          {bundle.steps.map((s, i) => (
            <li key={s.slug} className="step">
              <div className="step__num mono">
                {t("step")} {String(i + 1).padStart(2, "0")}
              </div>
              <div className="step__body">
                <h3 className="step__h">{s.header}</h3>
                <p className="step__b">{s.body}</p>
              </div>
              <div className="step__bar" />
            </li>
          ))}
        </ol>
      </section>

      <section className="exp__end">
        <div className="end__line" />
        <div className="end__close mono">{t("letter_close")}</div>
        <div className="end__sign">{t("letter_sign")}</div>
        <h2 className="end__title">{t("end_title")}</h2>
        <p className="end__sub">{t("end_sub")}</p>
        <div className="end__actions">
          <button
            className="big-cta"
            onClick={() => {
              expRef.current?.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            ↑ {t("end_cta")}
          </button>
          <button className="ghost mono" onClick={onSwitchAudience}>
            {t("switch_audience")}
          </button>
        </div>
        <div className="end__counter mono">
          <CounterTicker locale={bundle.locale} />
        </div>
      </section>
    </div>
  );
}

function ArgumentCard({
  a,
  idx,
  total,
  t,
  locale,
  saved,
  toggleSaved,
}: {
  a: ContentBundle["arguments_by_category"][string][number];
  idx: number;
  total: number;
  t: (k: string) => string;
  locale: string;
  saved: boolean;
  toggleSaved: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const share = (e: React.MouseEvent) => {
    e.stopPropagation();
    const text = `"${a.header}" — ${a.body}`;
    navigator.clipboard?.writeText(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <article className={`arg arg--${a.category}`}>
      <div className="arg__meta">
        <span className="mono">
          {t("argument_of")} {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </span>
        <span className="mono arg__cat">{a.category}</span>
      </div>
      <h3 className="arg__h">
        <span className="arg__quote">“</span>
        {a.header}
      </h3>
      <p className="arg__b">{a.body}</p>
      {a.stat_value && (
        <div className="arg__stat">
          <span className="arg__stat-v">{a.stat_value}</span>
          <span className="arg__stat-u mono">{a.stat_unit}</span>
        </div>
      )}
      <div className="arg__actions">
        <button
          className={`pill mono ${saved ? "pill--on" : ""}`}
          onClick={toggleSaved}
          aria-pressed={saved}
        >
          {saved ? "★ " + t("saved") : "☆ " + t("save")}
        </button>
        <button className="pill mono" onClick={share}>
          {copied ? "✓ " + t("shared") : "→ " + t("share")}
        </button>
      </div>
      {a.evidence_publisher && (
        <div className="arg__cite mono">
          {a.source_url ? (
            <a href={a.source_url} target="_blank" rel="noreferrer noopener">
              {a.evidence_publisher}
              {a.evidence_title ? ` — ${a.evidence_title}` : ""}
            </a>
          ) : (
            <span>
              {a.evidence_publisher}
              {a.evidence_title ? ` — ${a.evidence_title}` : ""}
            </span>
          )}
        </div>
      )}
      {a.used_fallback_locale && (
        <div className="arg__fallback mono">
          [{a.used_fallback_locale}] {localUi(locale, "fallback_hint")}
        </div>
      )}
    </article>
  );
}

function FaqList({
  faqs,
  t,
}: {
  faqs: ContentBundle["faqs"];
  t: (k: string) => string;
}) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <>
      <div className="faq__head">
        <div className="mono">— {t("faq_title").toUpperCase()}</div>
        <h3 className="faq__title">{t("faq_title")}</h3>
      </div>
      <div className="faq__list">
        {faqs.map((f, i) => {
          const isOpen = open === f.slug;
          return (
            <div key={f.slug} className={`faq__item ${isOpen ? "faq__item--open" : ""}`}>
              <button className="faq__q" onClick={() => setOpen(isOpen ? null : f.slug)}>
                <span className="faq__num mono">{String(i + 1).padStart(2, "0")}</span>
                <span className="faq__objection mono">{t("objection")}</span>
                <span className="faq__q-text">{f.question}</span>
                <span className="faq__toggle">{isOpen ? "−" : "+"}</span>
              </button>
              {isOpen && (
                <div className="faq__a">
                  <div className="mono faq__counter">{t("counter")}</div>
                  <div>
                    <p>{f.answer}</p>
                    {f.evidence_publisher && (
                      <div className="faq__cite mono">
                        {f.source_url ? (
                          <a
                            href={f.source_url}
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            {f.evidence_publisher}
                            {f.evidence_title ? ` — ${f.evidence_title}` : ""}
                          </a>
                        ) : (
                          <span>
                            {f.evidence_publisher}
                            {f.evidence_title ? ` — ${f.evidence_title}` : ""}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
